import type { ClientOrganization } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { OrganizationSearchBar } from '@/features/profile-setting/components/organization-search/OrganizationSearchBar';
import { OrganizationSearchEmptyState } from '@/features/profile-setting/components/organization-search/OrganizationSearchEmptyState';
import { OrganizationSearchHint } from '@/features/profile-setting/components/organization-search/OrganizationSearchHint';
import { OrganizationSearchResultList } from '@/features/profile-setting/components/organization-search/OrganizationSearchResultList';
import { useOrganizationSearch } from '@/features/profile-setting/components/organization-search/useOrganizationSearch';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { ExtendedModal } from '@/shared/components/ExtendedModal';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

export type Props = {
  visible?: boolean;
  organizations: ClientOrganization[] | undefined;
  onSelect: (id: string) => void;
  onDismiss: () => void;
  onCreatePress?: () => void;
  describeOrganization?: (org: ClientOrganization) => string | undefined;
};

export const OrganizationSearchModal: FC<Props> = ({
  visible,
  organizations,
  onSelect,
  onDismiss,
  onCreatePress,
  describeOrganization
}) => {
  const theme = useAppTheme();
  const { top } = useSafeAreaInsets();
  useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'components.organization-search'
  });

  const {
    search,
    setSearch,
    filtered,
    showHint,
    handleDismiss,
    handleSelect,
    handleCreatePress
  } = useOrganizationSearch({
    organizations,
    onSelect,
    onDismiss,
    onCreatePress
  });

  return (
    <ExtendedModal
      visible={visible}
      onDismiss={handleDismiss}
      onRequestClose={handleDismiss}
      statusBarTranslucent
      animationType="fade"
    >
      <View
        style={[
          styles.container,
          {
            paddingTop: top,
            backgroundColor: theme.colors.surfaceContainerLow
          }
        ]}
      >
        <OrganizationSearchBar
          value={search}
          onChangeText={setSearch}
          onBackPress={handleDismiss}
        />
        <KeyboardAwareScrollView keyboardShouldPersistTaps="handled">
          {showHint && <OrganizationSearchHint />}
          {!showHint && filtered.length === 0 && (
            <OrganizationSearchEmptyState onCreatePress={handleCreatePress} />
          )}
          {!showHint && (
            <OrganizationSearchResultList
              organizations={filtered}
              onSelect={handleSelect}
              describeOrganization={describeOrganization}
            />
          )}
        </KeyboardAwareScrollView>
      </View>
    </ExtendedModal>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 }
});
